import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  QueryList,
  TemplateRef,
  ViewChild }     from '@angular/core';
import { NgForm } from '@angular/forms';

import { WizardStepComponent } from './wizard.step.component';

@Component({
  selector   : 'wor-wizard',
  styles: [ require('./wizard.component.scss') ],
  template: require('./wizard.component.html')
})
export class WizardComponent implements AfterContentInit {
  @ContentChildren(WizardStepComponent) steps !: QueryList<WizardStepComponent>;

  // Company set up asks one or two questions a step, so the narrow dialog suits it
  // and stays the default. A screen that has to show a wide step, such as listing
  // every column in a customer's file against ours, asks for the full page width
  // instead. Nothing else about the wizard differs between the two.
  @Input() isFullWidth : boolean = false;

  @Output() indexChange = new EventEmitter<number>();
  @Output() onChange    = new EventEmitter<any>();

  @ViewChild('content') content !: ElementRef<HTMLElement>;

  index = 0;
  originalFormValue: any;

  get isFirst () : boolean {
    return this.index === 0;
  }

  get isInvalid () : boolean {
    if ( !this.content || ( this.isLast && this.isStepSkippable ) ) {
      return false;
    }

    const controls = this.getControlNames(this.content);

    if ( !this.ngForm || controls.length === 0 ) {
      return false;
    }

    for ( const name of controls ) {
      const control = this.ngForm.controls[ name ];

      if ( control && control.invalid ) {
        return true;
      }
    }

    return false;
  }

  get isLast () : boolean {
    return this.index === this.total - 1;
  }

  get isStepSkippable () : boolean {
    return this.steps.toArray()[ this.index ].isSkippable;
  }

  get progress () : number {
    return this.total
      ? Math.round(((this.index + 1) / this.total) * 100)
      : 0;
  }

  get template () : TemplateRef<any> {
    return this.steps.toArray()[ this.index ].template;
  }

  get total () : number {
    return this.steps
      ? this.steps.length
      : 0;
  }

  constructor (
    private changeDetectorRef : ChangeDetectorRef,
    @Optional()
    private ngForm            : NgForm
  ) {}

  ngAfterContentInit () : void {
    setTimeout(() => {
      if ( this.ngForm ) {
        this.originalFormValue = { ...this.ngForm.form.value };
      }
    });
  }

  back () : void {
    if ( this.isFirst ) {
      return;
    }

    const previousIndex = this.index;

    this.index--;

    this.changeDetectorRef.detectChanges();

    this.onChange.emit({
      action       : 'back',
      currentIndex : this.index,
      previousIndex: previousIndex
    });
  }

  getControlNames ( element : ElementRef<HTMLElement> ) : Array<string> {
    const names : Array<string> = [];
    const root  : HTMLElement   = element.nativeElement;

    const nodes = root.querySelectorAll<HTMLElement>('[name]');

    nodes.forEach(node => {
      const name = node.getAttribute('name');

      if ( name ) {
        names.push(name);
      }
    });

    return names;
  }

  next ( action = 'next' ) : void {
    if ( this.isLast ) {
      return;
    }

    const previousIndex = this.index;

    this.index++;

    this.indexChange.emit(this.index);

    this.onChange.emit({
      action       : action,
      currentIndex : this.index,
      previousIndex: previousIndex
    });
  }

  skip () : void {
    if ( !this.ngForm || !this.originalFormValue ) {
      this.next('skip');

      return;
    }

    const controls = this.getControlNames(this.content);

    controls.forEach(name => {
      const control = this.ngForm.controls[ name ];

      if ( !control ) {
        return;
      }

      const original = this.originalFormValue[ name ];

      control.reset(original, {
        emitEvent: false
      });

      control.markAsPristine();
      control.markAsUntouched();
    });

    this.next('skip');
  }
}
